import { ActionTree, MutationTree, GetterTree } from 'vuex';

import {
  ICardPerformance,
  IInitiativeFilter,
  IPerformanceState,
  IPerformanceStateConfig,
  IPerformanceTransaction,
  IPerformanceTransactionData
} from '@/models/performance';
import PaginationUtils from '@/utils/PaginationUtils';

import Module from '../interfaces/module.interface';
import { RootState } from '../types';

const defaultConfig = () => ({
  filters: {},
  pagination: PaginationUtils.defaultPagination(),
  scrollYPos: 0
});

export const initialState = (): IPerformanceState => ({
  customerCenter: {
    programLaunched: undefined,
    overview: undefined,
    list: [],
    initiatives: [],
    config: defaultConfig()
  },
  transactionsCenter: {
    config: defaultConfig(),
    list: []
  },
  conversions: {
    list: [],
    initiatives: [],
    config: defaultConfig()
  },
  activity: {
    customersSpend: undefined,
    customersSpendOpened: false,
    list: [],
    initiatives: [],
    config: defaultConfig()
  }
});

const actions: ActionTree<IPerformanceState, RootState> = {
  SET_OVERVIEW_DATA({ commit }, payload: ICardPerformance) {
    commit('setOverviewData', payload);
  },
  SET_ACTIVITY_CUSTOMERS_SPEND({ commit }, payload: Record<string, any>) {
    commit('setActivityCustomersSpend', payload);
  },
  SET_ACTIVITY_CUSTOMERS_SPEND_TOGGLE({ commit }, payload: boolean) {
    commit('setActivityCustomersSpendToggle', payload);
  },
  SET_CAMPAIGN_ACTIVITY_ACTIVE_TAB_KEY({ commit }, payload: string) {
    commit('setCampaignActivityActiveTabKey', payload);
  },
  SET_PROGRAM_LAUNCH({ commit }, payload: string) {
    commit('setProgramLaunch', payload);
  },
  SET_LIST_DATA(
    { commit, state },
    payload: {
      context: keyof typeof state;
      list: Array<Record<string, any>>;
    }
  ) {
    commit('setListData', payload);
  },
  SET_INITIATIVES(
    { commit, state },
    payload: {
      context: keyof typeof state;
      initiatives: Array<IInitiativeFilter>;
    }
  ) {
    commit('setInitiatives', payload);
  },
  RESET_CONFIG({ commit, state }, payload: { context: keyof typeof state }) {
    commit('resetConfig', payload);
  },
  SET_CONFIG(
    { commit, state },
    payload: { context: keyof typeof state; config: IPerformanceStateConfig }
  ) {
    commit('setConfig', payload);
  },
  UPDATE_TRANSACTION_LOYALTY_MODULE(
    { commit, state },
    payload: {
      context: keyof typeof state;
      dataToUpdate: Partial<IPerformanceTransaction>;
    }
  ) {
    const { dataToUpdate } = payload;
    const { list = [] } = state.transactionsCenter;
    const transactions = list.map(data => {
      return data.id === dataToUpdate.id
        ? {
            ...data,
            ...dataToUpdate
          }
        : data;
    });

    commit('setListData', {
      context: payload.context,
      list: transactions
    });
  }
};

const mutations: MutationTree<IPerformanceState> = {
  setOverviewData(state, overviewData: Record<string, ICardPerformance>) {
    state.customerCenter.overview = overviewData;
  },
  setActivityCustomersSpend(state, data: Record<string, any>) {
    state.activity.customersSpend = data;
  },
  setActivityCustomersSpendToggle(state, opened: boolean) {
    state.activity.customersSpendOpened = opened;
  },
  setCampaignActivityActiveTabKey(state, activeTabKey: string) {
    state.activity.campaignActivityActiveTabKey = activeTabKey;
  },
  setListData(
    state,
    payload: {
      context: keyof typeof state;
      list: Array<Record<string, any>>;
    }
  ) {
    state[payload.context].list = payload.list;
  },
  setInitiatives(
    state,
    payload: {
      context: keyof typeof state;
      initiatives: Array<IInitiativeFilter>;
    }
  ) {
    state[payload.context].initiatives = payload.initiatives;
  },
  setConfig(
    state,
    payload: { context: keyof typeof state; config: IPerformanceStateConfig }
  ) {
    Object.assign(state[payload.context].config, payload.config);
  },
  setProgramLaunch(state, payload: string) {
    state.customerCenter.programLaunched = payload;
  },
  resetConfig(state, payload: { context: keyof typeof state }) {
    Object.assign(state, {
      [payload.context]: {
        ...state[payload.context],
        config: defaultConfig()
      }
    });
  }
};

const getters: GetterTree<IPerformanceState, RootState> = {
  getCustomerCenterState: state => state.customerCenter,
  getConversionsState: state => state.conversions,
  getActivityState: state => state.activity,
  getListTransactionsCenterState: state => state.transactionsCenter
};

export const performance: Module<IPerformanceState, RootState> = {
  namespaced: true,
  state: initialState(),
  actions,
  mutations,
  getters
};
